import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { Col, Input, Select } from 'antd'
import { Employee, FullMeetingFragment } from '../../../../types'
import { useAuth } from '../../../../context/auth-context'
import { useDateTime } from '../../../../shared/useDateTime'
import { ManagerCloseMeeting } from '../shared/AppraisalMeetingService'
import { employeeSorter } from '../../../../shared/sortUtils'
import { MeetingModal } from '../shared/modals/MeetingModal'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import PerSpacer from '../../../../components/custom_components/Spacer'
import PerText from '../../../../components/custom_components/Text'
import useCompanyPartners from '../../../../graphql/employee/hooks/useCompanyPartners'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import PerDatePicker from '../../../../components/custom_components/DatePicker'
import PerTimePicker from '../../../../components/custom_components/TimePicker'
import { disableCurrent } from './util/meetingUtil'
import { computeFullname } from '../../../../shared/nameDisplayPreferencesUtil'
const { TextArea } = Input
const { Option } = Select

interface ManagerCloseMeetingModalProps {
  visible: boolean
  meeting: FullMeetingFragment
  onCancel: (cancel: boolean) => void
}

const ManagerCloseMeetingModal: FC<ManagerCloseMeetingModalProps> = ({ visible, meeting, onCancel }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const history = useHistory()
  const { now, utcDateStringToMoment } = useDateTime(user)
  const [meetingParticipant, setMeetingParticipant] = useState<string | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [partnerId, setPartnerId] = useState<number | undefined>(undefined)
  const initialTime = now()
  initialTime.minutes(Math.floor(initialTime.minutes() / 15) * 15)

  const [displayDate, setDisplayDate] = useState('')
  const [submittingCloseMeeting, setSubmittingCloseMeeting] = useState<boolean>(false)
  const { data, loading, error } = useCompanyPartners(meeting.employee.id)

  if (loading || error || !data || !data.companyPartners) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.companyPartners} error={error} />
  }

  const cancelConfirm = () => {
    onCancel(false)
    setPartnerId(undefined)
    setDisplayDate('')
    setErrorMessage('')
  }

  const closeMeetingAndRedirect = async (meeting: FullMeetingFragment) => {
    setSubmittingCloseMeeting(true)
    const { errors } = await ManagerCloseMeeting({
      id: meeting.id,
      additionalParticipants: meetingParticipant,
      partnerId: partnerId,
      displayDate: displayDate,
    })

    if (errors && errors.length) {
      const errorKey = errors[0].extensions?.validationErrors?.[0]?.errorKey
      const errorMessage = errorKey?.startsWith('error.code')
        ? errors[0].extensions?.validationErrors?.[0]?.message || t('appraisalMeeting.stepConclusion.modal.genericError')
        : t('appraisalMeeting.stepConclusion.modal.genericError')

      setErrorMessage(errorMessage)
      setSubmittingCloseMeeting(false)
    } else {
      history.push('/meetings')
      setSubmittingCloseMeeting(false)
    }
  }

  return (
    <MeetingModal
      title={t('appraisalMeeting.stepConclusion.modal.reviewer.confirmation.header')}
      visible={visible}
      onCancel={cancelConfirm}
      confirmDisabled={!partnerId || displayDate.length === 0 || submittingCloseMeeting || !!errorMessage}
      tooltip={
        !partnerId
          ? t('appraisalMeeting.stepConclusion.modal.reviewer.modalConfirmDisabledPartner')
          : displayDate.length === 0
          ? t('appraisalMeeting.stepConclusion.modal.reviewer.modalConfirmDisabledDisplayDate')
          : ''
      }
      onConfirm={() => closeMeetingAndRedirect(meeting)}
      submittingCloseMeeting={submittingCloseMeeting}
    >
      <>
        <PerFlexRow>{t('appraisalMeeting.stepConclusion.modal.reviewer.confirmation.message1')}</PerFlexRow>
        <PerFlexRow>
          <form className="fullWidth">
            <PerFlexRow>
              <PerSpacer className="fullWidth">
                <PerText mt="s" mb="xxs">
                  {t('appraisalMeeting.stepConclusion.selectPartners.header')}
                </PerText>
                <Select
                  value={partnerId}
                  placeholder={t('appraisalMeeting.stepConclusion.selectPartners.placeholder')}
                  onChange={(id: number) => setPartnerId(id)}
                  data-e2e-partner-select
                >
                  {[...data.companyPartners]
                    .sort((partnerA, partnerB) => employeeSorter(user.nameDisplayPreference, partnerA as Employee, partnerB as Employee))
                    .map((partner) => (
                      <Option key={partner.id} value={partner.id} data-e2e-partner-select-option>
                        {computeFullname(
                          user.nameDisplayPreference,
                          partner.chosenFirstName ? partner.chosenFirstName : partner.legalFirstName,
                          partner.chosenLastName ? partner.chosenLastName : partner.legalLastName,
                        )}
                      </Option>
                    ))}
                </Select>
                <PerText mt="s" mb="xxs">
                  {t('appraisalMeeting.stepConclusion.additionalParticipants')}
                </PerText>
                <TextArea
                  placeholder={t('appraisalMeeting.stepConclusion.setAddtitionalParticipants')}
                  value={meetingParticipant}
                  onChange={(val) => {
                    setMeetingParticipant(val.target.value)
                  }}
                  data-e2e-close-meeting-additional-participants
                  rows={2}
                />
              </PerSpacer>
            </PerFlexRow>

            <PerText mt="s" mb="xxs">
              {t('appraisalMeeting.stepConclusion.displayDate')}
            </PerText>
            <PerFlexRow>
              <Col span={12}>
                <PerDatePicker
                  inputDate={displayDate}
                  onDateChanged={(utcDateChanged) => {
                    !!utcDateChanged &&
                      setDisplayDate(
                        utcDateStringToMoment(utcDateChanged)!.minutes(initialTime.minutes()).tz('UTC').format('YYYY-MM-DDTHH:mm:00.000') +
                          'Z',
                      )
                  }}
                  timeZoneAndLocale={user}
                  dateFormat={'DMY'}
                  disabled={false}
                  disabledDate={(current) => disableCurrent(current, now(), utcDateStringToMoment(meeting.date))}
                />
              </Col>
              <Col span={12}>
                <PerTimePicker
                  inputDate={displayDate}
                  onTimeChanged={(utcTimeChanged) => {
                    !!utcTimeChanged && setDisplayDate(utcTimeChanged)
                  }}
                  timeZoneAndLocale={user}
                  timeFormat={'HH:mm'}
                  minuteStep={15}
                  hideDisabledOptions={true}
                />
              </Col>
            </PerFlexRow>
          </form>
        </PerFlexRow>

        {errorMessage && (
          <PerText color="error" fontSize="m" mt="m">
            {errorMessage}
          </PerText>
        )}
      </>
    </MeetingModal>
  )
}
export { ManagerCloseMeetingModal }
